import React from 'react';
import { observer } from 'mobx-react-lite';
import Grid from '@material-ui/core/Grid';
import LinearProgress from '@material-ui/core/LinearProgress';
import InfiniteScroll from 'react-infinite-scroller';
import debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import cssVars from 'css-vars-ponyfill';
import Message from '../Message/Message';
import ChatInput from '../ChatInput/ChatInput';
import styles from './MessageList.module.css';
import * as utils from '../../../../helpers/utils';
import { useRootStore } from '../../../../mobx/bridge';
import { formatMessagesSeparator } from '../../../../helpers/dateHelpers';

const LoadbarStyle = {
  barColorPrimary: `${styles.loadBarColor}`,
  colorPrimary: `${styles.loadBarBack}`,
};

const loadingBar = <LinearProgress key={0} classes={LoadbarStyle} />;

// eslint-disable-next-line prefer-arrow-callback,sonarjs/cognitive-complexity
const MessageList = observer(function MessageList() {
  const { commonStore, settingsStore, messageStore, sendingEvents } = useRootStore();
  const { t } = useTranslation();

  const staticFilteredMessageslength = React.useRef(0);
  commonStore.messagesContainerRef = React.useRef(null);

  const settings = settingsStore.getLoggedUserSettings;

  React.useLayoutEffect(() => {
    commonStore.scrollMessagesToBottom();
  });

  const getDateStringFromTimestamp = (timestamp) =>
    `${timestamp.getDay()}/${timestamp.getDate()}/${timestamp.getFullYear()}`;

  const loadNextBatchMessages = debounce(() => {
    if (commonStore.ignoreLoadMoreMessages) {
      commonStore.setIgnoreLoadMoreMessages(false);
      return;
    }

    if (settings && commonStore.messagesContainerRef && !commonStore.loadingChannelMessages) {
      commonStore.setOldScrollHeight(commonStore.messagesContainerRef.current.scrollHeight);

      const numberOfMessages =
        !staticFilteredMessageslength || staticFilteredMessageslength.current === 0
          ? 0
          : staticFilteredMessageslength.current;

      sendingEvents.socketEmitLoadMoreMessages(
        settings.SelectedChannel,
        numberOfMessages,
        utils.maxNumberOfItemsByContainerSize(document, commonStore.messagesContainerRef)
      );
    }
  }, 500);

  const handleRef = (incRef) => {
    if (incRef) {
      commonStore.messagesContainerRef.current = incRef;
    }
  };

  let previousDate;
  let filteredMessages = messageStore.computedGetFilteredMessagesForChannel(
    settings?.SelectedChannel
  );

  if (filteredMessages.length > commonStore.maxVisibleMessages) {
    filteredMessages = filteredMessages.slice(-commonStore.maxVisibleMessages);
  }

  staticFilteredMessageslength.current = filteredMessages.length;

  const groupedMessages = new Map();
  let firstMessage = filteredMessages.length > 0 ? filteredMessages[0] : null;
  let index = 1;
  let currentMessageDate;
  let previousMessageDate;
  let lastSeenMessageSeparator;

  if (firstMessage) {
    groupedMessages.set(index, {
      senderId: firstMessage.Sender ? firstMessage.Sender._id : firstMessage.MessageType.TypeName,
      messages: [firstMessage],
    });

    const firstMessageTimestamp = new Date(firstMessage.Timestamp);
    currentMessageDate = getDateStringFromTimestamp(firstMessageTimestamp);
    previousMessageDate = currentMessageDate;
  }

  for (let i = 1; i < filteredMessages.length; i += 1) {
    const currentMessageTimestamp = new Date(filteredMessages[i].Timestamp);

    currentMessageDate = getDateStringFromTimestamp(currentMessageTimestamp);

    if (
      previousMessageDate === currentMessageDate &&
      messageStore.computedCheckPreviousMessageType(firstMessage, filteredMessages[i])
    ) {
      const current = groupedMessages.get(index);
      if (current) {
        current.messages.push(filteredMessages[i]);
      }
    } else {
      index += 1;
      groupedMessages.set(index, {
        senderId: firstMessage.Sender ? firstMessage.Sender._id : firstMessage.MessageType.TypeName,
        messages: [filteredMessages[i]],
      });
    }
    firstMessage = filteredMessages[i];

    const firstTimestamp = new Date(firstMessage.Timestamp);
    previousMessageDate = getDateStringFromTimestamp(firstTimestamp);
  }

  const newRenderedMessages = [];

  for (let i = 1; i <= groupedMessages.size; i += 1) {
    const value = groupedMessages.get(i);

    const firstMessageInGroup = value.messages[0]._id;
    const lastMessageInGroup =
      value.messages.length > 1 ? value.messages[value.messages.length - 1]._id : null;

    let dateSeparator;

    const nextMessageDate = new Date(value.messages[0].Timestamp)
      .toLocaleString('en-US')
      .split(', ')[0];

    if (!previousDate || previousDate !== nextMessageDate) {
      previousDate = nextMessageDate;
      if (nextMessageDate !== new Date().toLocaleString('en-US').split(', ')[0]) {
        dateSeparator = (
          <h2 className={styles.dateSeparator}>
            <span className={styles.dateSeparatorText}>
              {formatMessagesSeparator(previousDate)}
            </span>
          </h2>
        );
      } else {
        dateSeparator = (
          <h2 className={styles.dateSeparator}>
            <span className={styles.dateSeparatorText}>{t('MessageList.Today')}</span>
          </h2>
        );
      }
    }

    const messageGroup = [];
    for (let j = 0; j < value.messages.length; j += 1) {
      const message = value.messages[j];

      lastSeenMessageSeparator = null;

      if (
        commonStore.lastSeenMessageForChannel &&
        commonStore.lastSeenMessageForChannel._id === message._id
      ) {
        lastSeenMessageSeparator = (
          <h2 className={styles.lastSeenSeparator}>
            <span className={styles.lastSeenSeparatorText}>{t('MessageList.NewMessages')}</span>
          </h2>
        );
      }

      const isFromInternal =
        message.Sender?.ContactType &&
        (message.Sender.ContactType.TypeId === utils.ContactTypeEnum.Internal.TypeId ||
          message.Sender.ContactType.TypeId === utils.ContactTypeEnum.Support.TypeId);

      const isFromLoggedUser =
        commonStore.getLoggedUser &&
        message.Sender &&
        message.Sender._id === commonStore.getLoggedUser._id;
      const isFirstMessage = firstMessageInGroup === message._id ? firstMessageInGroup : null;

      const isLastMessageInGroup = lastMessageInGroup === message._id ? lastMessageInGroup : null;

      messageGroup.push(
        <React.Fragment key={message._id}>
          {lastSeenMessageSeparator}

          <Message
            key={message._id}
            messageData={message}
            fromMe={isFromLoggedUser}
            isFirstMessage={isFirstMessage}
            isFromInternal={isFromInternal}
            showInitials={value.messages.length === 1 || isLastMessageInGroup}
            isHidden={settings?.HiddenMessages?.includes(message._id)}
          />
        </React.Fragment>
      );
    }

    newRenderedMessages.push(
      <div key={value.messages[0]._id}>
        {dateSeparator}

        {messageGroup}
      </div>
    );
  }

  const messageContainerStyles = [styles.messagesContainer];
  const hasImagePreviewStyles = [styles.hasImagePreview];

  if (commonStore.fullModeChatFileUpload) {
    cssVars({
      variables: { hasImagePreview: '204px' },
    });

    messageContainerStyles.push(hasImagePreviewStyles);
  } else {
    cssVars({
      variables: { hasImagePreview: '0px' },
    });
  }

  switch (true) {
    case commonStore.chatInputStyleHeight >= 180:
      messageContainerStyles.push(styles.hasMultiLineChatInput180);
      break;
    case commonStore.chatInputStyleHeight >= 160:
      messageContainerStyles.push(styles.hasMultiLineChatInput160);
      break;
    case commonStore.chatInputStyleHeight >= 140:
      messageContainerStyles.push(styles.hasMultiLineChatInput140);
      break;
    case commonStore.chatInputStyleHeight >= 120:
      messageContainerStyles.push(styles.hasMultiLineChatInput120);
      break;
    case commonStore.chatInputStyleHeight >= 100:
      messageContainerStyles.push(styles.hasMultiLineChatInput100);
      break;
    case commonStore.chatInputStyleHeight >= 80:
      messageContainerStyles.push(styles.hasMultiLineChatInput80);
      break;
    case commonStore.chatInputStyleHeight >= 60:
      messageContainerStyles.push(styles.hasMultiLineChatInput60);
      break;
    case commonStore.chatInputStyleHeight >= 40:
      messageContainerStyles.push(styles.hasMultiLineChatInput40);
      break;
    default:
      break;
  }

  return (
    <React.Fragment>
      {!commonStore.loadingChannelMessages || (
        <Grid
          component="div"
          container
          className={styles.loadScreenOverlay}
          justify="center"
          alignItems="flex-start"
        >
          <Grid component="div" item xs={12} align="center">
            {loadingBar}

            <span className={styles.loadScreenOverlay} />
          </Grid>
        </Grid>
      )}

      <Grid
        component="div"
        container
        className={commonStore.loadingChannelMessages ? styles.loadingScreen : ''}
      >
        <Grid component="div" item xs={12}>
          <div ref={handleRef} className={messageContainerStyles.join(' ')}>
            <InfiniteScroll
              loader={
                <span key="1" className={styles.loadingText}>
                  {t('Common.LoadingWithThreeDots')}
                </span>
              }
              loadMore={loadNextBatchMessages}
              hasMore={commonStore.channelHasMoreMessages}
              threshold={4}
              isReverse
              useWindow={false}
              initialLoad={false}
            >
              {newRenderedMessages}
            </InfiniteScroll>
          </div>
        </Grid>

        <Grid component="div" item xs={12}>
          <ChatInput />
        </Grid>
      </Grid>
    </React.Fragment>
  );
});
MessageList.whyDidYouRender = true;
MessageList.displayName = 'MessageList';

export default MessageList;
